export class AudioChunkAccumulator {
  private chunkStore: Int16Array[] = [];
  private readonly byteThreshold: number;

  public constructor(byteThreshold: number = 8192) {
    this.byteThreshold = byteThreshold;
  }

  public addChunk(event: AudioProcessingEvent): void {
    const chunk = AudioChunkAccumulator.fromAudioProcessingEventToPCM16(event);
    this.chunkStore.push(chunk);
  }

  public reset(): void {
    this.chunkStore = [];
  }

  public get isReadyToSend(): boolean {
    const totalBytes = this.chunkStore.reduce(
      (sum, chunk) => sum + chunk.byteLength,
      0
    );

    return totalBytes >= this.byteThreshold;
  }

  public get merged(): Int16Array {
    const totalLength = this.chunkStore.reduce(
      (sum, chunk) => sum + chunk.length,
      0
    );
    const merged = new Int16Array(totalLength);
    let offset = 0;
    for (const chunk of this.chunkStore) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    return merged;
  }

  // --- Métodos estáticos ---

  public static fromAudioProcessingEventToPCM16(
    event: AudioProcessingEvent
  ): Int16Array {
    const audioBuffer = event.inputBuffer;
    const channelData = audioBuffer.getChannelData(0);

    const pcm16 = new Int16Array(channelData.length);
    for (let index = 0; index < channelData.length; index++) {
      const s = Math.max(-1, Math.min(1, channelData[index] as number));
      pcm16[index] = s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff);
    }
    return pcm16;
  }

  public static int16ArrayToBase64(int16Array: Int16Array): string {
    const uint8Array = new Uint8Array(
      int16Array.buffer,
      int16Array.byteOffset,
      int16Array.byteLength
    );

    let binary = "";
    for (let index = 0; index < uint8Array.length; index++) {
      binary += String.fromCharCode(uint8Array[index] as number);
    }

    return btoa(binary);
  }

  public static fromMediaStreamToAudioScriptProcessor(
    stream: MediaStream
  ): ScriptProcessorNode {
    const audioContext = new window.AudioContext({
      sampleRate: 24000,
    });
    const source = audioContext.createMediaStreamSource(stream);
    const processor = audioContext.createScriptProcessor(8192, 1, 1);
    source.connect(processor);
    processor.connect(audioContext.destination);
    return processor;
  }

  public static isSilence(chunk: Int16Array, threshold = 0.01): boolean {
    let sumSquares = 0;
    for (let index = 0; index < chunk.length; index++) {
      const f = (chunk[index] as number) / 0x8000;
      sumSquares += f * f;
    }
    const rms = Math.sqrt(sumSquares / chunk.length);
    return rms < threshold;
  }
}
